import React, { useEffect, useState } from "react";
import { Button } from "@jugl-web/ui-components/cross-platform/Button";
import { SidebarDrawer } from "@jugl-web/ui-components/web/SidebarDrawer";
import { useForm } from "react-hook-form";
import {
  useRestApiProvider,
  enabledFieldTypes,
  FormField,
} from "@jugl-web/rest-api";
import { PlainButton, TextInput } from "@jugl-web/ui-components/cross-platform";
import {
  useToast,
  useTranslations,
  convertSnakeCaseToReadableString,
} from "@jugl-web/utils";
import { Select } from "@jugl-web/ui-components/cross-platform/forms/Select";
import {
  DropdownValueList,
  DropdownValueListItem,
} from "@jugl-web/domain-resources/tasks/components/ManageTaskFieldsSidebar/components/DropdownValueList";
import {
  InlineSidebar,
  InlineSidebarHeader,
} from "@jugl-web/ui-components/web/InlineSidebar";
import { useCustomerFields } from "@jugl-web/domain-resources/customers/hooks/useCustomerFields";
import { ReactComponent as BinIcon } from "./assets/bin.svg";
import { ConfirmDeleteFieldAlert } from "./ConfirmDeleteFieldAlert";

export const ManageFieldSidebar: React.FC<{
  entityId: string;
  isOpen: boolean;
  section: "personal" | "company";
  fieldToEdit?: null | FormField;
  onClose: () => void;
  onGoBack?: () => void;
}> = ({ entityId, isOpen, section, fieldToEdit, onClose }) => {
  const isLeadSource = fieldToEdit?.name === "lead_source";
  const { t } = useTranslations();
  const [savingInProgress, setSavingInProgress] = useState(false);
  const [isRemoveFieldConfirmAlertOpen, setRemoveFieldConfirmAlertOpen] =
    useState(false);
  const { customersFormFieldApi } = useRestApiProvider();
  const { formFields, fieldTypeLabelAndIcon } = useCustomerFields({
    entityId,
  });
  const { toast } = useToast({ variant: "web" });

  const [addFormField] = customersFormFieldApi.useAddFormFieldMutation();
  const [editFormField] = customersFormFieldApi.useEditFormFieldMutation();
  const {
    handleSubmit,
    register,
    formState: { isValid, errors },
    setValue,
    watch,
    reset,
  } = useForm({
    mode: "all",
    defaultValues: {
      name: "",
      type: "text" as FormField["type"],
      dropdownValues: [] as DropdownValueListItem[],
    },
  });
  const handleClose = () => {
    setSavingInProgress(false);
    reset({ name: "", type: "text", dropdownValues: [] });
    onClose();
  };
  const nameValue = watch("name");
  const normalizeName = (name: string) =>
    name.trim().toLowerCase().replace(" ", "_");
  const onSubmit = handleSubmit(async (data) => {
    setSavingInProgress(true);
    if (fieldToEdit) {
      const response = await editFormField({
        entityId,
        fieldId: fieldToEdit.id,
        params: {
          name: normalizeName(data.name),
          required: fieldToEdit.required,
          value: fieldToEdit.value,
          values:
            data.type === "dropdown"
              ? data.dropdownValues.map((v, i) => ({
                  value: v.text,
                  color: v.color,
                  order: i + 1,
                  id: v.id,
                }))
              : undefined,
        },
      });
      if ("data" in response) {
        toast(
          t({
            id: "feedback.field-updated",
            defaultMessage: "Field has been updated",
          })
        );
        handleClose();
      }
      setSavingInProgress(false);
      return;
    }
    const maxPosition = Math.max.apply(
      null,
      formFields
        ?.filter((item) => item.section === section)
        .map((item) => item.pos) || []
    );
    const normalizedName = normalizeName(data.name);
    const isNameAlreadyUsed = formFields?.find(
      (field) => field.name === normalizedName && field.section === section
    );

    if (isNameAlreadyUsed) {
      toast(
        t(
          {
            id: "feedback.field-name-already-exists",
            defaultMessage: "Field with name {name} already exists",
          },
          {
            name: data.name,
          }
        ),
        {
          variant: "error",
        }
      );
      setSavingInProgress(false);
      return;
    }
    if (entityId && data && maxPosition) {
      const response = await addFormField({
        entityId,
        params: {
          section,
          name: normalizedName,
          type: data.type,
          required: false,
          pos: maxPosition + 1,
          visibility: true,
          value: null,
          values:
            data.type === "dropdown"
              ? data.dropdownValues.map((v, i) => ({
                  value: v.text,
                  color: v.color,
                  order: i + 1,
                  id: crypto.randomUUID(),
                })) || null
              : null,
        },
      });

      if ("data" in response) {
        toast(
          t({
            id: "feedback.field-added",
            defaultMessage: "Field has been added",
          })
        );
        handleClose();
      }
    }
  });
  const params = watch();

  useEffect(() => {
    if (!isOpen) {
      reset({ name: "", type: "text", dropdownValues: [] });
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (fieldToEdit) {
      setValue("name", convertSnakeCaseToReadableString(fieldToEdit.name));
      setValue("type", fieldToEdit.type);
      if (fieldToEdit.type === "dropdown" && fieldToEdit.values) {
        setValue(
          "dropdownValues",
          fieldToEdit.values.map((v, i) => ({
            text: v.value,
            color: v.color,
            id: v.id,
            isReadonly: isLeadSource && i <= 2,
          }))
        );
      }
    }
  }, [setValue, fieldToEdit, isLeadSource]);

  return (
    <>
      <ConfirmDeleteFieldAlert
        isOpen={isRemoveFieldConfirmAlertOpen}
        field={fieldToEdit as FormField}
        onClose={() => setRemoveFieldConfirmAlertOpen(false)}
        entityId={entityId}
        onSuccess={() => {
          onClose();
        }}
      />
      <InlineSidebar isOpen={isOpen}>
        <InlineSidebarHeader
          onClose={handleClose}
          title={
            fieldToEdit
              ? t({
                  id: "customers-page.field-info",
                  defaultMessage: "Field Info",
                })
              : section === "personal"
                ? t({
                    id: "customers-page.new-field-personal-info",
                    defaultMessage: "New field to Personal Info",
                  })
                : t({
                    id: "customers-page.new-field-company-info",
                    defaultMessage: "New field to Company Info",
                  })
          }
          endSlot={
            fieldToEdit && !isLeadSource ? (
              <PlainButton onClick={() => setRemoveFieldConfirmAlertOpen(true)}>
                <BinIcon />
              </PlainButton>
            ) : undefined
          }
        />
        <SidebarDrawer.Content className="px-8 py-6">
          <div className="flex flex-col gap-6">
            <TextInput
              placeholder={t({
                id: "common.enter",
                defaultMessage: "Enter",
              })}
              label={t({
                id: "form-controls.field-name.label",
                defaultMessage: "Field name",
              })}
              autoFocus
              isRequired
              isInvalid={!!errors.name}
              isDisabled={isLeadSource}
              errorMessage={errors.name?.message as string}
              {...register("name", {
                required: true,
                pattern: /^\S+(?: \S+)*$/,
                maxLength: {
                  value: 200,
                  message: `${nameValue.length}/${200}`,
                },
              })}
            />
            <Select
              label={t({
                id: "form-controls.field-type.label",
                defaultMessage: "Field type",
              })}
              items={enabledFieldTypes.map((type) => ({
                icon: fieldTypeLabelAndIcon[type]?.icon,
                name: fieldTypeLabelAndIcon[type]?.name,
                value: type,
              }))}
              value={params.type || "text"}
              isRequired
              isDisabled={!!fieldToEdit}
              onSelect={(value) => setValue("type", value as FormField["type"])}
              {...register("type", { required: true })}
            />

            {params.type === "dropdown" && (
              <>
                <div className="px-3">
                  <span className="font-secondary text-xs leading-2 text-dark-500">
                    {t({
                      id: "tasks-page.dropdown-values",
                      defaultMessage: "Dropdown values",
                    })}
                  </span>
                </div>
                <DropdownValueList
                  values={params.dropdownValues}
                  isReadonly={false}
                  onValuesChange={(values: DropdownValueListItem[]) =>
                    setValue("dropdownValues", values)
                  }
                />
              </>
            )}
          </div>
          <Button
            className="mt-10 h-10"
            fullWidth
            isDisabled={
              savingInProgress ||
              !isValid ||
              (params.type === "dropdown" && params.dropdownValues.length === 0)
            }
            onClick={onSubmit}
          >
            {fieldToEdit
              ? t({
                  id: "common.save-changes",
                  defaultMessage: "Save changes",
                })
              : t({
                  id: "common.create",
                  defaultMessage: "Create",
                })}
          </Button>
        </SidebarDrawer.Content>
      </InlineSidebar>
    </>
  );
};
